import React, { useMemo, useCallback, useState, DragEvent, TouchEvent } from 'react'
import { useFieldArray, useForm, UseFormGetValues, UseFormReset, Control, UseFieldArrayReturn } from 'react-hook-form'
import { useDraggableDom } from '@/components/molecules/settings/use-draggable-dom'
import { SearchSetting } from '@/models/search-setting'

export interface UseSearchItemsManagementProps {
  initialSearchItems: SearchSetting[]
}

export interface UseSearchItemsManagementReturn {
  // フォーム管理
  control: Control<{ searchItems: SearchSetting[] }>
  formSearchItems: UseFieldArrayReturn<{ searchItems: SearchSetting[] }, 'searchItems'>
  getValues: UseFormGetValues<{ searchItems: SearchSetting[] }>
  reset: UseFormReset<{ searchItems: SearchSetting[] }>

  // キャンセル時に順序を戻すためのデフォルト値
  defaultSearchItems: SearchSetting[]
  setDefaultSearchItems: (items: SearchSetting[]) => void
  // 検索項目の順序が変更されたかどうかを判定
  isOrderChanged: boolean
  setIsOrderChanged: (isOrderChanged: boolean) => void
  // キャンセル時の処理
  onCancel: () => void

  // ドラッグ機能
  dragStart: (e: DragEvent<HTMLElement>) => void
  dragOver: (e: DragEvent<HTMLElement>) => void
  dragDrop: (e: DragEvent<HTMLElement>) => void
  handleTouchStart: (e: TouchEvent<HTMLElement>) => void
  handleTouchMove: (e: TouchEvent<HTMLElement>) => void
  handleTouchEnd: (e: TouchEvent<HTMLElement>) => void
}

/**
 * 検索項目管理用の共通Hook
 * 検索項目のフォーム管理、ドラッグ&ドロップ、トグル処理を統一
 */
export const useSearchItemsManagement = (props: UseSearchItemsManagementProps): UseSearchItemsManagementReturn => {
  const { initialSearchItems } = props

  // フォーム管理
  const { control, getValues, reset } = useForm<{ searchItems: SearchSetting[] }>({
    mode: 'all',
    defaultValues: {
      searchItems: initialSearchItems,
    },
  })

  const formSearchItems = useFieldArray({
    control,
    name: 'searchItems',
  })

  // デフォルト値の保存
  const [defaultSearchItems, setDefaultSearchItems] = useState<SearchSetting[]>(initialSearchItems)
  const [isOrderChanged, setIsOrderChanged] = useState<boolean>(false)

  // onChangeハンドラをメモ化
  const handleDragChange = useCallback(
    (newList: SearchSetting[]) => {
      setIsOrderChanged(true)
      formSearchItems.replace(newList)
    },
    [formSearchItems],
  )

  // ドラッグ&ドロップ機能
  const { dragStart, dragOver, dragDrop, handleTouchStart, handleTouchMove, handleTouchEnd } = useDraggableDom<SearchSetting>({
    list: formSearchItems.fields,
    onChange: handleDragChange,
    key: 'inputType',
  })

  /**
   * キャンセル処理
   */
  const onCancel = () => {
    reset({ searchItems: defaultSearchItems })
    setIsOrderChanged(false)
  }

  return {
    // フォーム管理
    control,
    formSearchItems,
    getValues,
    reset,

    // 状態
    defaultSearchItems,
    setDefaultSearchItems,
    // sort順の変更があったか
    isOrderChanged,
    setIsOrderChanged,
    onCancel,

    // ドラッグ機能
    dragStart,
    dragOver,
    dragDrop,
    handleTouchStart,
    handleTouchMove,
    handleTouchEnd,
  }
}
